import React, { useState } from 'react';
import {
  Brain, Sparkles, ShieldCheck, TrendingUp, MessageSquare,
  BarChart2, Star, ChevronDown, ChevronUp, Copy, CheckCheck,
  Zap, AlertTriangle, Target, type LucideIcon,
} from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { QuoteState, MultiCarrierResult, AIInsights } from '../types';

interface Props {
  quote:           QuoteState;
  currentSolution: MultiCarrierResult;
  insights?:       AIInsights;
}

const Section: React.FC<{
  icon:     LucideIcon;
  title:    string;
  color:    string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ icon: Icon, title, color, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl bg-[#0D0D1A] border border-white/8 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 hover:bg-white/3 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center`}>
            <Icon size={17} className="text-white" />
          </div>
          <span className="font-bold text-white text-sm">{title}</span>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/5 pt-4 animate-fade-in">{children}</div>}
    </div>
  );
};

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handle} className="p-1.5 rounded-lg text-gray-600 hover:text-purple-400 hover:bg-purple-500/10 transition-all">
      {copied ? <CheckCheck size={14} className="text-emerald-400" /> : <Copy size={14} />}
    </button>
  );
};

const PLACEHOLDER_INSIGHTS: AIInsights = {
  narrative: 'Generate a quote using the AI Assistant to unlock Executive Insights — including ROI analysis, objection handling scripts, and carrier comparison data tailored to your customer.',
  objectionHandling: [],
  industryBenefits: [],
  roiSlicing: '',
  carrierComparison: [],
};

export const CommandCenter: React.FC<Props> = ({ quote, currentSolution, insights }) => {
  const data = insights || PLACEHOLDER_INSIGHTS;

  const nxtMrc = quote.items.reduce((s, i) => s + i.mrc * i.lines, 0);
  const currTotal = currentSolution.combined.monthlyServiceTotal + currentSolution.combined.monthlyHardwareTotal + currentSolution.combined.taxesAndFees;
  const savings = currTotal - nxtMrc;

  const radarData = data.carrierComparison?.map(c => ({
    carrier: c.carrier,
    score:   c.score,
  })) || [];

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Executive Narrative */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-900/40 via-violet-900/20 to-indigo-900/10 border border-purple-500/20">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/30 shrink-0 mt-0.5">
            <Brain size={19} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-black text-white text-lg">AI Executive Summary</h2>
                <p className="text-xs text-purple-400">Powered by Konektra AI</p>
              </div>
              {data.narrative !== PLACEHOLDER_INSIGHTS.narrative && (
                <CopyButton text={data.narrative} />
              )}
            </div>
            <p className={`text-sm leading-relaxed ${insights ? 'text-gray-300' : 'text-gray-600 italic'}`}>
              {data.narrative}
            </p>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {insights && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Monthly Savings', value: `$${Math.max(0, savings).toFixed(2)}`, icon: TrendingUp, color: 'text-emerald-400' },
            { label: 'Annual ROI',      value: `$${(Math.max(0, savings) * 12).toFixed(2)}`, icon: Target,    color: 'text-purple-400' },
            { label: 'Top Carrier Score', value: `${data.carrierComparison?.reduce((best, c) => c.score > best ? c.score : best, 0) ?? 91}/100`, icon: Star, color: 'text-amber-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="p-4 rounded-2xl bg-white/5 border border-white/8 text-center">
              <Icon size={20} className={`${color} mx-auto mb-2`} />
              <div className={`text-xl font-black ${color}`}>{value}</div>
              <div className="text-xs text-gray-600 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Objection Handling */}
      {data.objectionHandling.length > 0 && (
        <Section icon={MessageSquare} title="Objection Handling Scripts" color="bg-gradient-to-br from-blue-500 to-cyan-600" defaultOpen>
          <div className="space-y-3">
            {data.objectionHandling.map((obj, i) => {
              const [objPart, resPart] = obj.includes('|') ? obj.split('|') : [obj, ''];
              return (
                <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/8">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
                      <span className="text-xs font-semibold text-amber-400">
                        {objPart.replace('Objection:', '').trim()}
                      </span>
                    </div>
                    <CopyButton text={resPart.replace('Response:', '').trim()} />
                  </div>
                  {resPart && (
                    <div className="flex items-start gap-2 pl-5">
                      <Zap size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-400 leading-relaxed">
                        {resPart.replace('Response:', '').trim()}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Industry Benefits */}
      {data.industryBenefits.length > 0 && (
        <Section icon={ShieldCheck} title="Industry-Specific Benefits" color="bg-gradient-to-br from-emerald-500 to-teal-600">
          <div className="grid sm:grid-cols-2 gap-3">
            {data.industryBenefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/8">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] font-black text-emerald-400">{i + 1}</span>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ROI Slicing */}
      {data.roiSlicing && (
        <Section icon={TrendingUp} title="ROI Analysis" color="bg-gradient-to-br from-purple-500 to-violet-600">
          <div className="p-4 rounded-xl bg-white/5 border border-white/8">
            <div className="flex justify-between mb-3">
              <span className="text-xs font-semibold text-gray-400">Financial Summary</span>
              <CopyButton text={data.roiSlicing} />
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">{data.roiSlicing}</p>
          </div>
        </Section>
      )}

      {/* Carrier Comparison */}
      {data.carrierComparison && data.carrierComparison.length > 0 && (
        <Section icon={BarChart2} title="Carrier Comparison" color="bg-gradient-to-br from-violet-500 to-purple-700">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Radar chart */}
            <div>
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="rgba(255,255,255,0.08)" />
                  <PolarAngleAxis dataKey="carrier" tick={{ fill: '#6B7280', fontSize: 11 }} />
                  <Radar dataKey="score" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.25} strokeWidth={2} />
                  <Tooltip
                    contentStyle={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }}
                    labelStyle={{ color: '#9CA3AF' }}
                    itemStyle={{ color: '#fff' }}
                    formatter={(v: number) => [`${v}/100`, 'Score']}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <div className="space-y-3">
              {data.carrierComparison.map(c => (
                <div key={c.carrier} className={`p-3 rounded-xl border ${c.score === Math.max(...(data.carrierComparison?.map(x => x.score) ?? [0])) ? 'bg-purple-500/10 border-purple-500/20' : 'bg-white/5 border-white/8'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-bold ${c.score === Math.max(...(data.carrierComparison?.map(x => x.score) ?? [0])) ? 'text-purple-300' : 'text-white'}`}>
                      {c.carrier}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-16 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${c.score >= 85 ? 'bg-emerald-500' : c.score >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${c.score}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-gray-400">{c.score}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-emerald-400">✓ </span>
                      <span className="text-gray-400">{c.strength}</span>
                    </div>
                    <div>
                      <span className="text-red-400">✗ </span>
                      <span className="text-gray-500">{c.weakness}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Placeholder when no insights */}
      {!insights && (
        <div className="text-center py-12 rounded-2xl bg-white/3 border border-dashed border-white/10">
          <Sparkles size={32} className="text-purple-500/40 mx-auto mb-3" />
          <p className="text-gray-600 text-sm font-medium">No AI insights yet</p>
          <p className="text-gray-700 text-xs mt-1 max-w-xs mx-auto">
            Use the AI Quote Assistant in the Builder tab to generate detailed insights, objection handling, and carrier analysis.
          </p>
        </div>
      )}
    </div>
  );
};
